import { RouteProp, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { useAtom } from "jotai";
import { useFragment } from "react-relay";

import {
  departmentToScheduleOption,
  ScheduleOption,
  ScheduleSwitcher,
} from "@/components/ScheduleSwitcher";
import { useScheduleNavigator } from "@/hooks/useScheduleNavigator";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { HoleCount } from "@whoosh/design-system/src/components/icons_v2/courseLayout";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import {
  courseAvailabilityQueryInput_groupSize,
  courseAvailabilityQueryInput_holeCount,
  courseAvailabilityQueryInput_timeRange,
} from "../atoms/courseAvailabilityQueryInputs";
import { DepartmentAvailabilityCoursePicker } from "./DepartmentAvailabilityCoursePicker";
import { DepartmentAvailabilityDatePicker } from "./DepartmentAvailabilityDatePicker";
import { DepartmentAvailabilityGroupSizeSelect } from "./DepartmentAvailabilityGroupSizeSelect";
import { DepartmentAvailabilityHoleCountSelect } from "./DepartmentAvailabilityHoleCountPicker";
import { DepartmentAvailabilityTimeSlider } from "./DepartmentAvailabilityTimeSlider";
import { DepartmentAvailabilitySidebarFragment_department$key } from "./__generated__/DepartmentAvailabilitySidebarFragment_department.graphql";
import { DepartmentAvailabilitySidebar_session$key } from "./__generated__/DepartmentAvailabilitySidebar_session.graphql";

type Props = {
  departmentRef: DepartmentAvailabilitySidebarFragment_department$key | null;
  sessionRef: DepartmentAvailabilitySidebar_session$key | null;
};

export const DepartmentAvailabilitySidebar = ({
  departmentRef,
  sessionRef,
}: Props) => {
  const route =
    useRoute<
      RouteProp<ClubStackNavigatorParamList, "DepartmentAvailability">
    >();
  const date = route?.params?.date;

  const department = useFragment(
    DepartmentAvailabilitySidebarFragmentString,
    departmentRef
  );

  const session = useFragment(
    DepartmentAvailabilitySidebarSessionFragmentString,
    sessionRef
  );

  const [holeCount, setHoleCount] = useAtom(
    courseAvailabilityQueryInput_holeCount
  );

  const [groupSize, setGroupSize] = useAtom(
    courseAvailabilityQueryInput_groupSize
  );

  const [timeRange, setTimeRange] = useAtom(
    courseAvailabilityQueryInput_timeRange
  );

  const onHoleCountChange = (value: HoleCount | null) => {
    setHoleCount(value ?? undefined);
  };

  const onGroupSizeChange = (value: number | undefined) => {
    setGroupSize(value);
  };

  const onTimeOfDayChange = ({
    timeLTE,
    timeGTE,
  }: {
    timeLTE?: string;
    timeGTE?: string;
  }) => {
    if (!timeLTE && !timeGTE) {
      setTimeRange(undefined);
    } else {
      setTimeRange({
        timeLTE,
        timeGTE,
      });
    }
  };

  const navigateToSchedule = useScheduleNavigator();

  const onScheduleSwitcherChange = (option: ScheduleOption) => {
    const curDate = new Date();
    const todayString = format(curDate, "yyyy-MM-dd");
    const navigateDate = date ? date : todayString;
    navigateToSchedule(option.type, option.slug, navigateDate);
  };

  const selectedScheduleOption = departmentToScheduleOption(department ?? null);

  return (
    <VStack gap={6} px={2}>
      <ScheduleSwitcher
        selectedOption={selectedScheduleOption}
        onChange={onScheduleSwitcherChange}
        sessionRef={session ?? null}
      />
      <DepartmentAvailabilityCoursePicker departmentRef={department ?? null} />
      <DepartmentAvailabilityGroupSizeSelect
        departmentRef={department ?? null}
        groupSize={groupSize}
        onChange={onGroupSizeChange}
      />
      <DepartmentAvailabilityHoleCountSelect
        holeCount={holeCount}
        onChange={onHoleCountChange}
      />
      <DepartmentAvailabilityTimeSlider
        departmentRef={department ?? null}
        timeRange={timeRange}
        onChange={onTimeOfDayChange}
      />
      <DepartmentAvailabilityDatePicker departmentRef={department ?? null} />
    </VStack>
  );
};

const DepartmentAvailabilitySidebarFragmentString = graphql`
  fragment DepartmentAvailabilitySidebarFragment_department on Department {
    ...DepartmentAvailabilityTimeSliderFragment_department
    ...DepartmentAvailabilityGroupSizeSelect_department
    ...departmentToScheduleOption_department
    ...DepartmentAvailabilityDatePicker_department
    ...DepartmentAvailabilityCoursePicker_department
  }
`;

const DepartmentAvailabilitySidebarSessionFragmentString = graphql`
  fragment DepartmentAvailabilitySidebar_session on Session {
    ...ScheduleSwitcher_session
  }
`;
